import { useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { api } from "~/utils/api";
import { useUser } from "~/modules/user/hooks/useUser";
import { useToaster } from "~/modules/toaster/hooks/useToaster";
import { getOauthClientDetailURL } from "~/routes";
import { LoadingSpinner } from "~/components/LoadingSpinner";
import { OauthClientTable } from "../../components/OauthClientTable";
import { RevokeConfirmDialog } from "../../components/RevokeConfirmDialog";
import { OauthClientListItem } from "../../types";
import { OnboardingEmptyState } from "./OnboardingEmptyState";
import { NoResultsState } from "./NoResultsState";
import { OauthClientsToolbar } from "./OauthClientsToolbar";
import { McpWizardModalProvider } from "~/modules/mcp/components/McpWizardModalProvider";
import { Button } from "~/components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-regular-svg-icons";

// // // //

type StatusFilter = "active" | "revoked" | "all";
type SortBy = "name" | "createdAt" | "lastUsedAt";

// // // //

function StatRow(props: { clients: number; active: number; sessions: number }) {
    const stats = [
        {
            label: "Total clients",
            value: props.clients,
            hint: "Registered OAuth apps",
        },
        { label: "Active", value: props.active, hint: "Currently authorized" },
        {
            label: "Live sessions",
            value: props.sessions,
            hint: "Across all clients",
        },
    ];
    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {stats.map((stat) => (
                <div
                    key={stat.label}
                    className="rounded-xl border bg-card p-4 shadow-2xs"
                >
                    <p className="text-sm text-muted-foreground">
                        {stat.label}
                    </p>
                    <p className="mt-1 font-mono text-2xl font-semibold tracking-tight text-foreground">
                        {stat.value}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                        {stat.hint}
                    </p>
                </div>
            ))}
        </div>
    );
}

// // // //

/**
 * OauthClientsList
 * List view: stat cards, filter/sort toolbar, the clients table, and empty states.
 */
function OauthClientsList() {
    const router = useRouter();
    const { user } = useUser();
    const { addToaster } = useToaster();

    const [status, setStatus] = useState<StatusFilter>("all");
    const [search, setSearch] = useState("");
    const [sortBy, setSortBy] = useState<SortBy>("createdAt");

    const [clientToRevoke, setClientToRevoke] =
        useState<OauthClientListItem | null>(null);

    // Fetch all clients for the user's current organization once; filtering +
    // sorting happen client-side below.
    const listQuery = api.oauthClients.list.useQuery({
        organization_id: user.organization.id,
        status: "all",
    });

    const revokeMutation = api.oauthClients.revoke.useMutation({
        onSuccess: () => {
            setClientToRevoke(null);
            listQuery.refetch();
        },
        onError: (error) => {
            addToaster({
                variant: "danger",
                message:
                    error.message || "Something went wrong. Please try again.",
            });
        },
    });

    const allClients = useMemo(() => listQuery.data ?? [], [listQuery.data]);

    const clients = useMemo(() => {
        const query = search.trim().toLowerCase();
        const filtered = allClients.filter((client) => {
            const matchesQuery =
                !query ||
                client.name.toLowerCase().includes(query) ||
                client.clientId.toLowerCase().includes(query) ||
                (client.description?.toLowerCase().includes(query) ?? false);
            const matchesStatus = status === "all" || client.status === status;
            return matchesQuery && matchesStatus;
        });

        return [...filtered].sort((a, b) => {
            switch (sortBy) {
                case "name":
                    return a.name.localeCompare(b.name);
                case "lastUsedAt":
                    return (
                        new Date(b.lastUsedAt ?? 0).getTime() -
                        new Date(a.lastUsedAt ?? 0).getTime()
                    );
                default:
                    return (
                        new Date(b.createdAt).getTime() -
                        new Date(a.createdAt).getTime()
                    );
            }
        });
    }, [allClients, search, status, sortBy]);

    const activeCount = allClients.filter(
        (client) => client.status === "active",
    ).length;
    const totalSessions = allClients.reduce(
        (sum, client) => sum + client.activeSessionCount,
        0,
    );

    const isLoading = listQuery.isLoading;
    const hasNoClientsAtAll = !listQuery.isLoading && allClients.length === 0;

    function goToDetail(client: OauthClientListItem) {
        router.push(getOauthClientDetailURL({ clientId: client.id }));
    }

    if (isLoading) {
        return (
            <div className="flex justify-center">
                <LoadingSpinner size={24} />
            </div>
        );
    }

    if (hasNoClientsAtAll) {
        return <OnboardingEmptyState />;
    }

    return (
        <div className="flex flex-col gap-6">
            <StatRow
                clients={allClients.length}
                active={activeCount}
                sessions={totalSessions}
            />

            <div className="overflow-hidden rounded-xl border bg-card shadow-2xs">
                <OauthClientsToolbar
                    search={search}
                    status={status}
                    sortBy={sortBy}
                    resultCount={clients.length}
                    onSearchChange={setSearch}
                    onStatusChange={setStatus}
                    onSortByChange={setSortBy}
                />

                {clients.length === 0 && <NoResultsState />}

                {clients.length > 0 && (
                    <OauthClientTable
                        clients={clients}
                        onView={goToDetail}
                        onRevoke={setClientToRevoke}
                    />
                )}
            </div>

            {clientToRevoke && (
                <RevokeConfirmDialog
                    open={true}
                    onOpenChange={(open) => {
                        if (!open) setClientToRevoke(null);
                    }}
                    clientName={clientToRevoke.name}
                    loading={revokeMutation.isLoading}
                    onConfirm={() =>
                        revokeMutation.mutate({ id: clientToRevoke.id })
                    }
                />
            )}
        </div>
    );
}

// // // //

export function OauthClientsListPage() {
    return (
        <div className="mt-6 flex flex-col gap-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="max-w-2xl">
                    <h2 className="text-xl font-semibold text-foreground">
                        OAuth Clients
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Create and manage OAuth clients for connecting MCP
                        clients like Claude to RWA.xyz.
                    </p>
                </div>

                <McpWizardModalProvider>
                    {({ openWizard }) => (
                        <Button
                            size="lg"
                            variant="primaryLight"
                            onClick={() => openWizard("claude")}
                        >
                            <FontAwesomeIcon
                                className="size-4"
                                icon={faPlus}
                                aria-hidden="true"
                            />
                            New Connection
                        </Button>
                    )}
                </McpWizardModalProvider>
            </div>

            <OauthClientsList />
        </div>
    );
}
